import React, { useState } from 'react';
import LayoutUsuario from '../../components/navegacao/LayoutUsuario';
import Titulo from '../../components/tipografia/Titulo';
import Descricao from '../../components/tipografia/Descricao';
import VBox from '../../components/layout/VBox';
import HBox from '../../components/layout/HBox';
import Grid, { GridItem } from '../../components/layout/Grid';
import Botao from '../../components/Botao';
import { mdiOfficeBuilding, mdiCogOutline, mdiEmailOutline, mdiKeyOutline, mdiCreditCardOutline, mdiBellAlertOutline } from '@mdi/js';
import Icon from '../../components/Icon';
import styles from './page.module.css';

// Componentes das seções
import SecaoOrganizacao from './SecaoOrganizacao';
import SecaoGeral from './SecaoGeral';
import SecaoPreferenciasEmail from './SecaoPreferenciasEmail';
import SecaoChavesApi from './SecaoChavesApi';
import SecaoContaPagamentos from './SecaoContaPagamentos';
import SecaoAlertas from './SecaoAlertas';
import { useAlertas } from '@/lib/useAlertas';
import { useAuth } from '@/lib/useAuth';
import { hasAuthorization, rolesAdmin, rolesManager } from '@tera/shared/src/lib/auth';

type SecaoAtiva = 'organizacao' | 'geral' | 'preferencias-email' | 'chaves-api' | 'conta-pagamentos' | 'alertas';

const Page = () => {
    const [secaoAtiva, setSecaoAtiva] = useState<SecaoAtiva>('organizacao');
    const { user } = useAuth();
    const { ativos: alertasAtivos } = useAlertas();

    const secoes = [
        {
            id: 'organizacao' as SecaoAtiva,
            label: 'Organização',
            icon: mdiOfficeBuilding,
            description: 'Dados cadastrais da empresa',
        },
        {
            id: 'preferencias-email' as SecaoAtiva,
            label: 'Preferências de Email',
            icon: mdiEmailOutline,
            description: 'Gerenciar notificações por email',
        },
        {
            id: 'geral' as SecaoAtiva,
            label: 'Geral',
            icon: mdiCogOutline,
            description: 'Configurações gerais do sistema',
        },
        {
            id: 'chaves-api' as SecaoAtiva,
            label: 'Chaves API',
            icon: mdiKeyOutline,
            description: 'Chaves para integrações externas',
        },
        {
            id: 'conta-pagamentos' as SecaoAtiva,
            label: 'Conta e Pagamentos',
            icon: mdiCreditCardOutline,
            description: 'Plano contratado e histórico de faturas',
        },
        {
            id: 'alertas' as SecaoAtiva,
            label: 'Alertas',
            icon: mdiBellAlertOutline,
            description: alertasAtivos.length > 0
                ? `${alertasAtivos.length} alerta(s) ativo(s)`
                : 'Problemas detectados pelo sistema',
        },
    ];

    const renderSecaoAtiva = () => {
        switch (secaoAtiva) {
            case 'organizacao':
                return <SecaoOrganizacao />;
            case 'preferencias-email':
                return <SecaoPreferenciasEmail />;
            case 'geral':
                return <SecaoGeral />;
            case 'chaves-api':
                return <SecaoChavesApi />;
            case 'conta-pagamentos':
                return <SecaoContaPagamentos />;
            case 'alertas':
                return <SecaoAlertas />;
            default:
                return <SecaoOrganizacao />;
        }
    };

    if (!hasAuthorization(user, rolesAdmin)) {
        return (
            <LayoutUsuario>
                <VBox center style={{ height: '100vh' }}>
                    <h2 style={{ textAlign: 'center', color: 'var(--tc-color-primary)' }}>Opa! Área restrita!</h2>
                    <img src='access-denied-min.png' style={{ height: 384 }} />
                    <p>Parece que você não tem acesso a esta página.</p>
                    <p><b>- Isso é um engano!</b> - você pode dizer. Nesse caso, chame o admin ou o suporte para te ajudar.</p>
                </VBox>
            </LayoutUsuario>
        );
    }

    return (
        <LayoutUsuario>
            <VBox gap="16px" style={{ height: 'calc(100vh - 32px)' }}>
                {/* Header */}
                <Grid>
                    <GridItem sm={12}>
                        <VBox>
                            <Titulo>Configurações</Titulo>
                            <Descricao>
                                Gerencie as configurações da organização e do sistema
                            </Descricao>
                        </VBox>
                    </GridItem>
                </Grid>

                {/* Conteúdo principal */}
                <Grid style={{ flex: 1 }}>
                    {/* Menu lateral das seções */}
                    <GridItem sm={12} md={3}>
                        <VBox gap="8px" style={{
                            padding: '16px',
                            backgroundColor: 'var(--tc-color-gray-50)',
                            borderRadius: '8px',
                            border: '1px solid var(--tc-color-gray-200)',
                        }}>
                            {secoes.map((secao) => {
                                const comAlerta = secao.id === 'alertas' && alertasAtivos.length > 0;
                                return (
                                <Botao
                                    key={secao.id}
                                    variant={secaoAtiva === secao.id ? 'primary' : 'outline-secondary'}
                                    icon={comAlerta ? undefined : secao.icon}
                                    onClick={() => setSecaoAtiva(secao.id)}
                                    style={{
                                        width: '100%',
                                        textAlign: 'left',
                                        justifyContent: 'flex-start',
                                        padding: '16px',
                                        minHeight: '60px',
                                        height: 'auto'
                                    }}
                                >
                                    <HBox gap="8px" style={{ alignItems: 'center', width: 'auto' }}>
                                    {comAlerta ? (
                                        <span className="notif notif-danger" style={{ flexShrink: 0 }}>
                                            <Icon path={secao.icon} size={1.5} color="var(--tc-color-danger)" />
                                        </span>
                                    ) : null}
                                    <VBox gap={0.25}>
                                        <span>{secao.label}</span>
                                        <small style={{ opacity: 0.7, fontSize: '12px', lineHeight: 1.2 }}>
                                            {secao.description}
                                        </small>
                                    </VBox>
                                    </HBox>
                                </Botao>
                                );
                            })}
                        </VBox>
                    </GridItem>

                    {/* Conteúdo da seção ativa */}
                    <GridItem sm={12} md={9}>
                        <div style={{
                            padding: '16px',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            border: '1px solid var(--tc-color-gray-200)',
                            minHeight: '400px',
                        }}>
                            {renderSecaoAtiva()}
                        </div>
                    </GridItem>
                </Grid>
            </VBox>
        </LayoutUsuario>
    );
};

export default Page;